// @givmo/app-charity — auth state context + provider (+ charity auth ACTIONS).
//
// The context tracks the live Firebase session (`onAuthStateChanged`), exposes `isReady` for the
// cold-load gate + `isAuthenticated` for the route guard, AND exposes the charity auth ACTIONS the
// sign-in/sign-up screens drive: email sign-in/up, provider sign-in (Google/Microsoft), password
// reset, and backend OTP send/confirm.
//
// DIVERGENCE FROM THE ADMIN ARCHETYPE (deliberate): the admin app couples sign-UP to employer-company
// REGISTRATION (`adminSignUpWithEmail` provisions the Givmo row with Firebase-user rollback, and the
// company is registered once the emailed code proves the work email, NF-FIX21) and resolves
// a post-sign-in ROUTING state from the user's company (`decideRoutingState`). The charity portal has
// NEITHER: a charity admin is a Firebase user with charity memberships provisioned by the CLAIM flow
// (CPA-WEB2 / CPA-BE2). The `/charity-admin/*` backend (CPA-BE1) IS live and consumed elsewhere in
// this app, but charity SIGN-IN/UP deliberately does NOT use it. So the
// charity auth actions are the framework-agnostic @givmo/auth PRIMITIVES with NO company coupling:
//   - email sign-in         → the shared `signInWithEmail` ceremony (AS-SUITE2), parameterised with
//                             this door's rememberMe persistence and NO tail — the company coupling
//                             the admin portal adds is that door's `finish`, not the ceremony's;
//   - email sign-up         → raw firebase/auth (`createUserWithEmailAndPassword` + `updateProfile`);
//   - Google / Microsoft    → `signInWithStrategy` (popup-first, same-site redirect fallback) — the
//                             super-admin pattern;
//   - backend OTP           → @givmo/auth's `adminSendVerificationEmail`/`adminConfirmEmailCode` over
//                             the OTP-only `CharityAuthBackend` (generic `/users/me/*` endpoints);
//   - password reset        → `adminSendPasswordReset`;
//   - passkey sign-in       → @givmo/auth's UNBOUND `signInWithPasskey` over `@givmo/passkeys`'
//                             assertion transport, with its dark-gate probe (AS-WEB12) — Board's
//                             director door places it; the console's `/login-home` does not;
//   - organization sign-in  → @givmo/auth's `signInWithOrganizationSso` over `@givmo/passkeys`' SSO
//                             transport (AS-WEB30): the employer's own sign-in page, its handoff
//                             exchanged for a custom token. Every door places it where it can work.
// The route guard consumes this live user to distinguish signed-out, unverified password, and
// verified/provider sessions.

import {
  createContext,
  use,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  updateProfile,
  type Auth,
  type User,
  type UserCredential,
} from 'firebase/auth';
import {
  AuthError,
  adminSendPasswordReset,
  adminSendVerificationEmail,
  adminConfirmEmailCode,
  googleProvider,
  microsoftProvider,
  needsBackendOtpVerification,
  persistenceForRememberMe,
  reauthenticateWithPassword,
  reauthenticateWithProvider,
  reconfirmWithPasskey,
  sessionSignInProvider,
  startTotpEnrollment,
  unenrollTotp,
  signInWithEmail,
  signInWithStrategy,
  probePasskeySignIn,
  signInWithPasskey,
  signInWithOrganizationSso,
  type AdminAuthBackend,
  type AdminProviderId,
  type PasskeyAssertionTransport,
  type PasskeyAvailability,
  type PasskeyReconfirmationTransport,
  adoptSuiteSession,
  createSuiteSessionCancelHandle,
  establishSuiteSession,
  establishSuiteSessionAfterProvisioning,
  releaseSuiteSession,
  type SuiteSessionCancelHandle,
  type SuiteSessionEstablishOutcome,
  type SuiteSessionTransport,
} from '@givmo/auth';
import {
  createOrganizationSsoTransport,
  createPasskeyAssertionTransport,
  createPasskeyReconfirmationTransport,
  type AuthenticatorControls,
} from '@givmo/passkeys';
import { createCharityAuthBackend } from '../features/auth/charity-backend';
import { ensureBackendUser } from './ensure-backend-user';

export interface SignInWithEmailParams {
  email: string;
  password: string;
  rememberMe: boolean;
}

export interface SignUpWithEmailParams {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

/** The charity auth actions exposed to the screens. */
export interface AuthActions {
  /** Email/password sign-in. Applies rememberMe persistence, then signs in (no company routing). */
  signInWithEmail: (params: SignInWithEmailParams) => Promise<UserCredential>;
  /** Email/password sign-up: create the Firebase user + set the display name (no company registration). */
  signUpWithEmail: (params: SignUpWithEmailParams) => Promise<UserCredential>;
  /** Provider sign-in (Google / Microsoft), popup-first with same-site redirect fallback. */
  signInWithProvider: (id: AdminProviderId) => Promise<UserCredential>;
  /**
   * Passkey SIGN-IN (AS-WEB12): `@givmo/auth`'s UNBOUND assertion — whichever account asserts is
   * signed in — over `@givmo/passkeys`' `/authn` transport. The session it starts goes through the
   * auth-state chokepoint like every other sign-in (the backend `users` row, the suite session, with
   * the provider doors' persistence: no checkbox, so the app's configured scope). Never a step-up:
   * the sheet's passkey arm is {@link AuthActions.confirmWithPasskey}.
   */
  signInWithPasskey: () => Promise<UserCredential>;
  /**
   * Organization SIGN-IN (AS-WEB30): the handoff the console's `/sso/callback` received, exchanged
   * with the step's PKCE verifier for a custom token, then signed in. Like the passkey sign-in, its
   * session goes through the auth-state chokepoint; rejects with the exchange's own refusal (the
   * door reads its state from it) or an `AuthError`.
   */
  signInWithOrganizationSso: (params: {
    handoffCode: string;
    codeVerifier: string;
  }) => Promise<UserCredential>;
  /**
   * Whether this browser and the backend can run a passkey sign-in (`probePasskeySignIn`): the
   * door's dark gate reads it through `@givmo/passkeys`' `usePasskeySignInAvailability`, which
   * caches the verdict. Never rejects.
   */
  probePasskeyAvailability: () => Promise<PasskeyAvailability>;
  /** Trigger the backend OTP email (email verification). */
  sendOtp: () => Promise<void>;
  /**
   * Whether THIS app instance has already started an OTP send for the signed-in user — recorded the
   * moment `sendOtp` is called, before its first await; cleared on sign-out, and when that send
   * fails. The verify screen's mount auto-send defers to it (`features/auth/verify-email-screen.tsx`):
   * the sign-up form's `otpSent` search flag can reach that screen AFTER it has mounted, because the
   * session's publish re-runs the public-route guard, which mounts `/verify-email` on its own with
   * only `redirect` in hand (CPA-WEB59). The URL cannot be the source of truth for "a send already
   * happened" — the guard that mounts the screen cannot know one did, and under a fast round trip
   * the flag arrives nested inside a `redirect` — but the context that performs the send can.
   */
  otpSendAttempted: () => boolean;
  /** Confirm the backend OTP code. Throws on an invalid code. */
  confirmOtp: (code: string) => Promise<void>;
  /**
   * "Try again" for the backend `users` row bootstrap — what the memberships error card's retry
   * runs (`org/active-org-context.tsx`). Re-runs `ensureBackendUser` for the signed-in user (a no-op
   * when an earlier attempt already succeeded — the per-uid memo — and the real retry when the one at
   * sign-in failed), then, once the row certainly exists, re-establishes the suite session
   * (AS-SUITE11): a first sign-in whose bootstrap FAILED had its chained establish refused for want
   * of a row, and the chokepoint's once-per-uid guard never fires again, so the account held no suite
   * session until its next real sign-in. Sends nothing when the attempt retained for this sign-in
   * already landed, exactly one login otherwise, and never waits on it. Establishes only for the
   * account still signed in when the bootstrap answers. Within the 300 s freshness bound every
   * establish carries: a retry that heals the row later than that sends nothing, and the account
   * establishes on its next real sign-in. Rejects with the bootstrap's failure; resolves at once when
   * signed out.
   */
  retryBackendUserBootstrap: () => Promise<void>;
  /** Send the password-reset email. */
  sendPasswordReset: (email: string) => Promise<void>;
  /**
   * STEP-UP: re-confirm the CURRENT session with its own password, refreshing the ID token's
   * `auth_time` so a step-up-gated action succeeds on retry. Not a sign-in — it cannot swap the
   * session, and it leaves `rememberMe` persistence alone. Rejects with `sessionExpired` when signed
   * out, and with a `SecondFactorRequiredError` an enrolled admin finishes via the code prompt.
   */
  reauthenticateWithPassword: (password: string) => Promise<UserCredential>;
  /** STEP-UP through the admin's OAuth provider. Popup-only — see `@givmo/auth`'s `reauth.ts`. */
  reauthenticateWithProvider: (id: AdminProviderId) => Promise<UserCredential>;
  /**
   * STEP-UP with one of the account's OWN passkeys (AS-WEB10, D-AS-17): `@givmo/auth`'s bound
   * `reconfirmWithPasskey` — the minted token's account checked before the exchange, one exchange,
   * a forced refresh — and then the suite session login re-run for the new session, with this
   * sign-in's remember-me choice and cancel handle, so later handoffs from this console carry its
   * freshness. The step-up sheet's passkey slot runs it. Never the unbound passkey sign-in.
   */
  confirmWithPasskey: () => Promise<void>;
  /**
   * AS-WEB14: authenticator-app enrolment for the LIVE session (`auth.currentUser`, never a render
   * snapshot: the "Confirm it's you" sheet re-issues the session before the retry), handed to the
   * Sign-in & security card. Optional, so a console's test doubles need not supply it.
   */
  authenticator?: AuthenticatorControls;
  /**
   * Mint a FRESH ID token for the live session, bypassing the cached one. Callers that must not be
   * served the token a backend just rejected (the step-up retry) drive this before re-issuing the
   * request. Resolves `null` when signed out. Reads `auth.currentUser` for the same reason
   * {@link requireCurrentUser} does — a render-state snapshot can be stale by the time an async
   * failure handler runs, and refreshing the wrong user's token would silently do nothing.
   *
   * After a re-sign-in confirmation (a password or provider re-authentication, the code included)
   * it also re-runs the suite session login, once, so later handoffs carry the confirmation's
   * freshness; a passkey confirmation already re-ran it.
   */
  refreshIdToken: () => Promise<string | null>;
}

export interface AuthState extends AuthActions {
  /** The current Firebase user, or `null` when signed out / not yet resolved. */
  user: User | null;
  /** `true` once the initial `onAuthStateChanged` emission has resolved (cold-load gate). */
  isReady: boolean;
  /** Whether a user is currently signed in. The route guard keys off this. */
  isAuthenticated: boolean;
  /**
   * CPA-WEB86: a sign-in Firebase has completed that this provider has not published yet — its
   * `users` row bootstrap is still in flight, and the session publishes only once that settles — as
   * the promise of what it publishes: the user, or `null` if the session ends first. `null` when no
   * sign-in is waiting. A door navigates the moment its sign-in resolves, so a guard reading the
   * signed-out snapshot waits on this instead (`decideOnSettledSession`). Read live, never from a
   * render. Absent from the test doubles and the preview's mock, which have no sign-in in flight.
   */
  pendingSignIn?: () => Promise<User | null> | null;
  /** Sign out of the Firebase session. */
  signOut: () => Promise<void>;
}

/** What a route guard decides on: whether someone is signed in, and who. */
export type GuardSession = Pick<AuthState, 'isAuthenticated' | 'user'>;

/**
 * Run a route guard's decision on the session it must decide on (CPA-WEB86): the snapshot, unless
 * it reads signed out while a sign-in Firebase has completed is still publishing, when the guard
 * waits for that publish and decides on it. Deciding on the snapshot there sent a person who had
 * just signed in to a door for the bootstrap's round trip (~300 ms on dev), against this portal's
 * rule that a signed-in person never sees one. Synchronous when nothing is waiting, so every other
 * arrival is decided exactly as before; otherwise a promise, which `beforeLoad` awaits while the
 * screen the person signed in on stays up.
 */
export function decideOnSettledSession<T>(
  auth: AuthState,
  decide: (session: GuardSession) => T,
): T | Promise<T> {
  const pending = auth.isAuthenticated ? null : (auth.pendingSignIn?.() ?? null);
  if (pending === null) return decide(auth);
  return pending.then((user) => decide({ isAuthenticated: user !== null, user }));
}

/**
 * Whether this Firebase user must complete the backend-OTP verification gate. The provider table
 * (which methods' unverified email the OTP repairs — password AND microsoft.com, since GCIP mints
 * Microsoft users unverified; attesting providers pass; and, NF-FIX24, any account whose email no
 * linked attesting provider vouches for, such as a phone-linked app account) lives in
 * `@givmo/auth`'s `needsBackendOtpVerification`, shared with the admin portal's provider flows.
 * Without this gate a Microsoft claimant, or an app user, could never Tier-1 auto-verify a charity
 * claim and hit opaque refusals on every verified-email backend gate.
 */
export function requiresEmailVerification(user: User | null): boolean {
  return user !== null && needsBackendOtpVerification(user);
}

const AuthContext = createContext<AuthState | null>(null);

/** Build the configured charity provider. Google + Microsoft are the charity set (no Apple/Facebook). */
function charityProvider(id: AdminProviderId) {
  return id === 'google' ? googleProvider() : microsoftProvider();
}

/**
 * The LIVE Firebase user for a step-up, or a typed refusal. Deliberately `auth.currentUser` and not
 * the `user` render state: a step-up runs from an async action-failure handler, where the captured
 * state may be a stale snapshot, and re-authenticating the wrong `User` object is exactly the
 * identity confusion `@givmo/auth`'s reauth primitives exist to prevent.
 */
function requireCurrentUser(auth: Auth): User {
  const current = auth.currentUser;
  if (current === null) throw new AuthError('sessionExpired');
  return current;
}

export interface AuthProviderProps {
  auth: Auth;
  children: ReactNode;
  /**
   * Optional observer hook called when Firebase emits. Router invalidation must not be wired here:
   * React has not committed the new context yet, so the app owns that in a uid-keyed layout effect.
   */
  onAuthChange?: () => void;
  /** Test seam: override the transport adapter. Production builds the OTP-only adapter from the SDK. */
  backend?: AdminAuthBackend;
  /**
   * Test seam: override the passkey re-confirmation transport. Production binds the two
   * `/users/me/passkeys/reauthentication*` operations (`@givmo/passkeys`).
   */
  passkeyReconfirmation?: PasskeyReconfirmationTransport;
  /**
   * Test seam: override the passkey SIGN-IN transport. Production posts the two `/authn` assertion
   * routes through `givmoClient` (`@givmo/passkeys`' `createPasskeyAssertionTransport`).
   */
  passkeyAssertion?: PasskeyAssertionTransport;
  /**
   * The suite-session transport (AS-SUITE4). Present in the real app (`main.tsx` builds it from the
   * env base URL + the app's App Check provider); OMITTED in tests and the preview harness, where it
   * disables the whole rail so this provider behaves exactly as it did before AS-SUITE4.
   */
  suiteSession?: SuiteSessionTransport;
}

export function AuthProvider({
  auth,
  children,
  onAuthChange,
  backend,
  passkeyReconfirmation,
  passkeyAssertion,
  suiteSession,
}: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [isReady, setIsReady] = useState(false);
  /**
   * The boot handoff, held as ONE in-flight promise rather than a "tried it" boolean.
   *
   * A second subscription (React StrictMode's deliberate double-mount) JOINS this attempt instead of
   * either firing a second request against a rate-limit budget shared with real sign-ins, or —
   * worse — skipping adoption and publishing a signed-out state while the first attempt is still in
   * flight, which would flash the door at a user who is signed in elsewhere. Same shape as this
   * app's own `ensureBackendUser`: one in-flight promise, joined by concurrent callers.
   */
  const bootHandoffRef = useRef<Promise<UserCredential | null> | null>(null);
  /**
   * True once the auth state has resolved ONCE, by any route — an adoption that answered, or a first
   * emission that already carried a user.
   *
   * ⚠ Both routes must set it, and the second one is the subtle half. Adoption is a BOOT behaviour;
   * a sign-out is not a boot. If this were set only on the adoption path, then a property that booted
   * with a persisted session — the ordinary returning-user case — would still be "unsettled" when the
   * user later signs out, and that sign-out's `null` emission would take the adopt branch and hand
   * the session straight back. With a healthy release that is merely a wasted request (the row is
   * already gone, so the handoff answers "no session"); with a FAILED release it re-adopts
   * immediately and the sign-out button looks broken.
   */
  const bootSettledRef = useRef(false);
  /** The uid a suite session was already established for — one establish per sign-in, not per emission. */
  const establishedUidRef = useRef<string | null>(null);
  /**
   * The establish retained for the current sign-in — the chokepoint's chained attempt, or the one a
   * retried bootstrap made after it — so `retryBackendUserBootstrap` can hand it over as `attempted`
   * and send nothing for a session that already landed (AS-SUITE11).
   */
  const establishInFlightRef = useRef<Promise<SuiteSessionEstablishOutcome> | null>(null);
  /**
   * The current sign-in's cancel handle (AS-SUITE11) — every establish for the sign-in is admitted
   * through it, and `signOut` hands it to the release.
   *
   * Establish is fire-and-forget, which is right for the sign-in it rides on and wrong for the
   * release that may follow it: releasing while the login POST is still in flight deletes nothing,
   * and the login then lands and sets the cookie anyway (AS-SUITE9). The release therefore cancels
   * this first and is sent only once any login it admitted has settled; a login NOT yet sent — still
   * behind the bootstrap, its ID-token refresh or its App Check mint — is simply never sent, so the
   * sign-out waits on nothing in front of the wire. Renewed only when a new sign-in arrives after the
   * old one ended — so a retried bootstrap that outlives its sign-in may find ANOTHER account's live
   * handle here, which is why `retryBackendUserBootstrap` first checks that its account is still the
   * one signed in (AS-SUITE11 review F1); otherwise it finds it cancelled.
   */
  const establishCancelRef = useRef<SuiteSessionCancelHandle | null>(null);
  /**
   * The persistence choice to mirror onto the suite session. Only the email door has the checkbox;
   * the provider doors pass no persistence at all and so inherit the app's configured scope, which
   * is Firebase's `local` — so `true` is the honest mirror for them, and the default here.
   *
   * ⚠ Getting this wrong is a real defect, not a rounding error: `true` emits a cookie with
   * `Max-Age=1209600`, which survives a browser restart. Sending it for a user who UNCHECKED
   * "remember me" would silently sign the next person on a shared computer in as them.
   */
  const rememberMeRef = useRef(true);
  /**
   * The uid an OTP send was started for on this instance (see `otpSendAttempted`). A uid, not a
   * boolean, so a different account signing in on the same device never inherits it; cleared on
   * sign-out so a returning unverified user's guard arrival still auto-sends.
   */
  const otpSendStartedForUidRef = useRef<string | null>(null);
  /**
   * A re-sign-in confirmation (password or provider) has run since the suite login was last re-run,
   * so the next forced refresh — the step-up gate's, after the prompt — re-runs it once. A passkey
   * confirmation re-runs it itself and clears this.
   */
  const suiteRefreshOwedRef = useRef(false);
  /**
   * CPA-WEB86: the user React has COMMITTED as published — `user` is a render snapshot. A guard
   * waiting on a sign-in is released only once the commit that carries it has run, because that
   * commit is also the render that hands the router its new context (`RoutedApp`): released earlier,
   * a redirect the guard then throws would meet the next guard still reading signed out.
   */
  const committedUserRef = useRef<User | null>(null);
  /** CPA-WEB86: the guards waiting on a sign-in's publish; each checks again whenever woken. */
  const publishWaitersRef = useRef(new Set<() => void>());
  /**
   * CPA-WEB88: the user the latest auth-state emission carried, `null` once signed out. A sign-in
   * publishes only when its `users` row bootstrap settles, and by then the session may have ended,
   * or another account signed in: its publish then belongs to a session that is over. Every later
   * emission publishes for itself (a sign-out at once, a sign-in after its own bootstrap), so
   * dropping a superseded publish never leaves the session unpublished.
   */
  const latestEmissionRef = useRef<User | null>(null);

  useLayoutEffect(() => {
    committedUserRef.current = user;
    for (const wake of [...publishWaitersRef.current]) wake();
  }, [user]);

  useEffect(() => {
    const publishSignedOut = () => {
      setUser(null);
      setIsReady(true);
      onAuthChange?.();
      // CPA-WEB86: a guard waiting on a sign-in whose session just ended decides on that now. No
      // commit may follow to wake it: `user` can already be null.
      for (const wake of [...publishWaitersRef.current]) wake();
    };

    // Subscribe once. The stream emits the current user asynchronously on subscription, so this also
    // covers cold boot with a persisted Firebase session (parity with the admin/super-admin apps).
    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      latestEmissionRef.current = nextUser;
      if (nextUser === null) {
        establishedUidRef.current = null;
        otpSendStartedForUidRef.current = null;
        // AS-SUITE4: a property that boots signed-out asks whether this browser holds a suite
        // session BEFORE `isReady` flips — someone signed in on another `*.givmo.io` property must
        // never see this door, not even for a frame. Every failure, the dark 404 included, answers
        // `null` and the door renders exactly as it does today.
        if (suiteSession !== undefined && !bootSettledRef.current) {
          const attempt = (bootHandoffRef.current ??= adoptSuiteSession({ auth, ...suiteSession }));
          void attempt.then((credential) => {
            bootSettledRef.current = true;
            // A credential re-enters THIS listener with a user, which runs the ensure-then-publish
            // path below — the SAME bootstrap chokepoint every other sign-in goes through, which is
            // why adoption needs no bootstrap of its own. Publishing a signed-out state here as well
            // is what would flash the door.
            if (credential === null) publishSignedOut();
          });
          return;
        }
        publishSignedOut();
        return;
      }
      // Bootstrap the backend `users` row BEFORE publishing the session (see ensure-backend-user.ts:
      // a cold visitor has none, and every /charity-admin read 403s without it). Publishing after the
      // ensure settles means the memberships/claims queries the routes fire can never race it. A
      // FAILED ensure still publishes — the memberships error card is the honest surface for that,
      // and its "Try again" re-runs the ensure (the failure cleared the per-uid memo). One that goes
      // unanswered fails at its bound (CPA-WEB89, `BOOTSTRAP_REQUEST_TIMEOUT_MS`).
      const bootstrap = ensureBackendUser(nextUser.uid, nextUser.email).catch(() => undefined);
      // AS-SUITE4: every completed sign-in arrives here — email, the MFA-resolved continuation, the
      // provider popups, a custom-token exchange — so this is the one place that covers all of them.
      // Fire-and-forget: the door already succeeded and a suite session is a convenience on top of
      // it. The primitive refuses BEFORE the wire on a restored session (its `auth_time` is outside
      // the backend's freshness bound) and on an adopted one (it carries the handoff marker), which
      // is what makes a call from here safe rather than chatty.
      //
      // AS-SUITE7: it runs AFTER the bootstrap settles, never beside it. This portal provisions the
      // Givmo `user` row right here, on the emission — a cold visitor's first sign-in (email
      // sign-up, a first Google/Microsoft sign-in) arrives with no row — and the backend refuses to
      // establish for an identity without one, deliberately and uniformly with a malformed token.
      // Fired beside the bootstrap, the establish reached the wire before the row existed, was
      // refused, and the once-per-uid guard never fired again: the new account held no suite
      // session until a later sign-in. Chaining it costs nothing on the other paths — a restored
      // or adopted session is still refused before the wire, and the bootstrap's round trip is
      // already on the road to publishing. It runs whether or not the bootstrap SUCCEEDED: a
      // failed `GET /users/me` is not proof the row is absent, and the backend adjudicates.
      if (suiteSession !== undefined && establishedUidRef.current !== nextUser.uid) {
        establishedUidRef.current = nextUser.uid;
        // AS-SUITE11: a sign-in after the last one ENDED gets a fresh cancel handle; otherwise it
        // shares the live one, so a sign-out stops every login this tab has in motion.
        if (establishCancelRef.current === null || establishCancelRef.current.cancelled) {
          establishCancelRef.current = createSuiteSessionCancelHandle();
        }
        const cancelHandle = establishCancelRef.current;
        // Retained, not floated: see `establishInFlightRef`. Still fire-and-forget for this listener
        // — nothing here awaits it, and the sign-in publishes below regardless of how it resolves.
        establishInFlightRef.current = bootstrap.then(() =>
          establishSuiteSession({
            user: nextUser,
            rememberMe: rememberMeRef.current,
            cancelHandle,
            ...suiteSession,
          }),
        );
      }
      // The auth state has resolved; a later `null` is a sign-out, not a boot. See `bootSettledRef`.
      bootSettledRef.current = true;
      void bootstrap.then(() => {
        // CPA-WEB88: publish only while this sign-in is still the session. A sign-out during the
        // bootstrap has already published signed out, and woken any guard waiting on this sign-in;
        // publishing now would sign the console back in as the account that left. An account that
        // signed in since publishes for itself, after its own bootstrap.
        if (latestEmissionRef.current !== nextUser) return;
        setUser(nextUser);
        setIsReady(true);
        onAuthChange?.();
      });
    });
    return unsubscribe;
  }, [auth, onAuthChange, suiteSession]);

  const value = useMemo<AuthState>(() => {
    const adapter = backend ?? createCharityAuthBackend(auth);
    const reconfirmation = passkeyReconfirmation ?? createPasskeyReconfirmationTransport();
    const assertion = passkeyAssertion ?? createPasskeyAssertionTransport();
    const organizationSso = createOrganizationSsoTransport();
    /**
     * Re-run the suite session login for the live session after a confirmation, admitted through
     * this sign-in's cancel handle (a sign-out in progress sends nothing). Fire-and-forget, retained
     * like the chokepoint's: the confirmation already succeeded, and the suite session is a
     * convenience on top of it. The primitive's own refusals still apply (a handed-off session, a
     * stale `auth_time`).
     */
    const rerunSuiteLogin = () => {
      suiteRefreshOwedRef.current = false;
      const current = auth.currentUser;
      if (suiteSession === undefined || current === null) return;
      const cancelHandle = establishCancelRef.current;
      if (cancelHandle === null || cancelHandle.cancelled) return;
      establishInFlightRef.current = establishSuiteSession({
        user: current,
        rememberMe: rememberMeRef.current,
        cancelHandle,
        ...suiteSession,
      });
    };
    return {
      user,
      isReady,
      isAuthenticated: user !== null,
      // CPA-WEB86: owed while Firebase holds a session this provider has not committed — the
      // chokepoint above is still bootstrapping its `users` row, or has not yet heard of it.
      pendingSignIn: () => {
        const owed = () => {
          const current = auth.currentUser;
          return current !== null && committedUserRef.current?.uid !== current.uid;
        };
        if (!owed()) return null;
        return new Promise<User | null>((resolve) => {
          const wake = () => {
            if (owed()) return;
            publishWaitersRef.current.delete(wake);
            resolve(auth.currentUser === null ? null : committedUserRef.current);
          };
          publishWaitersRef.current.add(wake);
        });
      },
      // AS-SUITE4: release the suite session BEFORE the local sign-out. `use-sign-out.ts` is the
      // portal's ONE sign-out and it calls this, so every chrome inherits the release from here
      // rather than from a call site. Order is the point: afterwards the property may already have
      // torn down the state that got it here, and a property that signed out locally first could
      // silently re-establish the session the user just ended. It never rejects, so sign-out cannot
      // fail because a convenience layer did.
      // AS-SUITE9 + AS-SUITE11: and no login may land after the release, or the release deletes
      // nothing and the login then sets the cookie anyway — a sign-out that leaves a live suite
      // session behind, which on a shared machine is the next person signed in as this one. The
      // release takes this sign-in's cancel handle: a login not yet sent (behind the bootstrap, its
      // ID-token refresh or its App Check mint — a retried bootstrap's included) never is, and one
      // already on the wire is waited for — cut by its own 8 s write timeout — before the release
      // goes. Never a timer on the wait: a timer releases while a login whose refresh finishes late
      // is still to be SENT. Worst case ≈ 16 s to the local `firebaseSignOut` (a black-holed login
      // sent just before the click, then a black-holed release; the release's own mint is bounded
      // and overlaps the wait) — was ≈ 46 s plus two unbounded mints.
      signOut: async () => {
        if (suiteSession !== undefined) {
          await releaseSuiteSession({ ...suiteSession, cancelHandle: establishCancelRef.current });
        }
        await firebaseSignOut(auth);
      },
      // ── Charity auth actions ────────────────────────────────────────────────────────────────────
      // The shared `@givmo/auth` ceremony (AS-SUITE2): persistence scope, credential, and the
      // second-factor challenge a charity admin who enrolled TOTP on mobile owes (AS-WEB1). Note
      // what it deliberately does NOT bring with it — this stays the company-COUPLING-free path the
      // header describes, because the admin flow's registration/routing tail is `admin.ts`'s
      // `finish` argument to the same ceremony, not part of the ceremony itself.
      // AS-SUITE4: record the checkbox so the suite session mirrors this door's persistence choice.
      // `false` yields a browser-SESSION suite cookie; `true` a 14-day one. This is the only door
      // that has the control, and the establish call at the chokepoint reads what it left here.
      signInWithEmail: ({ email, password, rememberMe }) => {
        rememberMeRef.current = rememberMe;
        return signInWithEmail(
          auth,
          { email, password },
          { persistence: persistenceForRememberMe(rememberMe) },
        );
      },
      signUpWithEmail: async ({ email, password, firstName, lastName }) => {
        try {
          const credential = await createUserWithEmailAndPassword(auth, email, password);
          await updateProfile(credential.user, { displayName: `${firstName} ${lastName}` });
          return credential;
        } catch (error) {
          throw AuthError.from(error);
        }
      },
      // Popup-only is intentional: no `authDomain` is passed, so `signInWithStrategy` never escalates
      // to the redirect fallback (which requires a SAME-SITE custom authDomain). Charity's authDomain
      // is a cross-site `*.firebaseapp.com` (CPA-WEB8), for which the redirect path is broken-by-
      // default on modern browsers — hence popup-first with no fallback here.
      signInWithProvider: (id) => signInWithStrategy(auth, charityProvider(id)),
      // AS-WEB12: the unbound passkey sign-in. Like the provider doors it changes neither persistence
      // nor `rememberMeRef`; the custom-token session it starts reaches the chokepoint above, which
      // bootstraps the row and establishes the suite session exactly as for any other sign-in. No
      // second-factor branch: GCIP raises none on a custom-token sign-in (`agent_docs/auth.md`).
      signInWithPasskey: () => signInWithPasskey(auth, assertion),
      probePasskeyAvailability: () => probePasskeySignIn(assertion),
      // AS-WEB30: the organization sign-in — the custom-token session reaches the chokepoint above
      // like the passkey's, with the same unchanged persistence (the doors have no checkbox).
      signInWithOrganizationSso: (params) =>
        signInWithOrganizationSso(auth, organizationSso, params),
      // The OTP endpoints 403 without a backend `users` row, and the sign-up flow calls this right
      // after Firebase account creation — possibly before the auth-state listener's own bootstrap
      // has settled. Ensuring here joins that same in-flight attempt (per-uid dedupe), so the send
      // is ordered after the row exists on every path.
      sendOtp: async () => {
        const current = requireCurrentUser(auth);
        // Recorded BEFORE the first await, so a verify screen mounted while this send is in flight
        // already sees it (`otpSendAttempted`); a failed send clears it so a later arrival may try.
        otpSendStartedForUidRef.current = current.uid;
        try {
          await ensureBackendUser(current.uid, current.email);
          await adminSendVerificationEmail(adapter);
        } catch (error) {
          if (otpSendStartedForUidRef.current === current.uid) {
            otpSendStartedForUidRef.current = null;
          }
          throw error;
        }
      },
      otpSendAttempted: () => {
        const current = auth.currentUser;
        return current !== null && otpSendStartedForUidRef.current === current.uid;
      },
      confirmOtp: (code) => adminConfirmEmailCode(adapter, code),
      // AS-SUITE11: the memberships "Try again" (`org/active-org-context.tsx`) lives outside this
      // context, so a bootstrap it healed never reached the chokepoint's establish — which had already
      // run, been refused for want of a row, and recorded the uid. This is that retry's road back in:
      // the bootstrap, then the post-provisioning helper with the attempt retained for this sign-in
      // (sends nothing if it landed, one login otherwise), admitted through the sign-in's cancel
      // handle. Only after a bootstrap that SUCCEEDED: a failed retry is not the row appearing, and
      // the chokepoint already spent this sign-in's attempt on the uncertain case.
      retryBackendUserBootstrap: async () => {
        const current = auth.currentUser;
        if (current === null) return;
        await ensureBackendUser(current.uid, current.email);
        // AS-SUITE11 review F1: the bootstrap can outlive the sign-in it was healing, and by the time
        // it answers ANOTHER account may be signed in here — whose sign-in renewed the handle read
        // below. Establishing now would send this account's login through that sign-in's open
        // handle, after this account's own release: the next person at the machine adopted as it.
        // Only the account still signed in establishes.
        if (auth.currentUser?.uid !== current.uid) return;
        if (suiteSession === undefined) return;
        // The sign-in's handle: if a sign-out has begun ending it (the release cancels the handle
        // before the local sign-out runs), it is cancelled, and the helper answers `cancelled` at once
        // without sending anything.
        const cancelHandle = (establishCancelRef.current ??= createSuiteSessionCancelHandle());
        const attempted =
          establishedUidRef.current === current.uid ? establishInFlightRef.current : null;
        establishInFlightRef.current = establishSuiteSessionAfterProvisioning({
          user: current,
          rememberMe: rememberMeRef.current,
          attempted,
          cancelHandle,
          ...suiteSession,
        });
      },
      sendPasswordReset: (email) => adminSendPasswordReset(auth, email),
      // ── Step-up re-authentication (AS-WEB3) ─────────────────────────────────────────────────────
      // Both delegate to @givmo/auth's reauth primitives, which bind to the passed user and raise a
      // second-factor challenge the caller's prompt completes. The sign-in actions above are NOT an
      // acceptable substitute (they can swap the session and re-apply persistence) — see reauth.ts.
      // `async` so a signed-out refusal REJECTS rather than throwing synchronously out of the call
      // expression — these run from async failure handlers that only guard the promise.
      reauthenticateWithPassword: async (password) => {
        const current = requireCurrentUser(auth);
        suiteRefreshOwedRef.current = true;
        return reauthenticateWithPassword(auth, current, password);
      },
      reauthenticateWithProvider: async (id) => {
        const current = requireCurrentUser(auth);
        suiteRefreshOwedRef.current = true;
        return reauthenticateWithProvider(auth, current, charityProvider(id));
      },
      confirmWithPasskey: async () => {
        await reconfirmWithPasskey(auth, reconfirmation);
        rerunSuiteLogin();
      },
      authenticator: {
        uid: user?.uid ?? null,
        sessionSignInProvider: () => sessionSignInProvider(auth.currentUser),
        start: async () => startTotpEnrollment(requireCurrentUser(auth)),
        turnOff: async () => unenrollTotp(requireCurrentUser(auth)),
      },
      refreshIdToken: async () => {
        const token = (await auth.currentUser?.getIdToken(true)) ?? null;
        if (token !== null && suiteRefreshOwedRef.current) rerunSuiteLogin();
        return token;
      },
    };
  }, [auth, user, isReady, backend, passkeyReconfirmation, passkeyAssertion, suiteSession]);

  return <AuthContext value={value}>{children}</AuthContext>;
}

/** Read the auth state. Throws if used outside an {@link AuthProvider}. */
export function useAuth(): AuthState {
  const ctx = use(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used within an <AuthProvider>.');
  }
  return ctx;
}

export { AuthContext };
